import { AppDispatch, RootState } from '@/store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { showToast } from '@/helper/toast.helper';
import { usePermission } from '@/hooks/usePermission';
import { getProjectsDropdown } from '@/store/slices/projectsSlice/projectsSlice';
import {
  fetchSitesList,
  getSitesByProjectId,
  getSitesCount,
  toggleSiteStatus,
} from '@/store/slices/siteManagementSlice/siteManagementSlice';
import { ProjectDropdownDto, Site } from '@/types/project.types';
import { HasGeofence, SiteByProjectIdDto, SiteInfo, SiteListItemDto } from '@/types/site.types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowDown, Eye, Folder, MapPin, Shield } from 'lucide-react';
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import MapSiteTile from '../CommonComponents/MapSiteTile';
import DropdownWithRadio from '../CommonComponents/RadioButtons';
import ServerSearchFilter from '../CommonComponents/ServerSearchFilter';
import SiteInfoCard from '../CommonComponents/SiteInfoCard';
import StatCard from '../CommonComponents/StatCard';
import ViewDetailsModal from '../CommonComponents/ViewDetailsModal';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

type StatusOption = {
  value: boolean;
  label: string;
} | null;

const MapEventsHandler = React.memo(
  ({
    selectedLatLng,
    setTilePosition,
  }: {
    selectedLatLng: L.LatLng | null;
    setTilePosition: (pos: { x: number; y: number }) => void;
  }) => {
    const map = useMap();

    useEffect(() => {
      if (!selectedLatLng) return;

      const updatePosition = () => {
        const point = map.latLngToContainerPoint(selectedLatLng);
        const rect = map.getContainer().getBoundingClientRect();

        setTilePosition({
          x: rect.left + point.x,
          y: rect.top + point.y,
        });
      };

      updatePosition();

      map.on('move', updatePosition);
      map.on('zoom', updatePosition);

      return () => {
        map.off('move', updatePosition);
        map.off('zoom', updatePosition);
      };
    }, [map, selectedLatLng, setTilePosition]);

    return null;
  },
);

export default function SiteManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { list, sitesStats, sitesByProject, loadingSitesByProject } = useSelector(
    (state: RootState) => state.sites,
  );
  const { list: projectsList } = useSelector((state: RootState) => state.projects);
  const sitesData = list.data;

  const projectsData = projectsList.projectsDropdown;
  const selectedProjectsSitesData = sitesByProject;
  const [statusFilter, setStatusFilter] = useState<StatusOption>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedProject, setSelectedProject] = useState<ProjectDropdownDto | null>(null);
  const [selectedSite, setSelectedSite] = useState<{ value: any; label: string } | null>(null);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const [openSiteModal, setOpenSiteModal] = useState(false);
  const [filterBy, setFilterBy] = useState<'Sites' | 'Users' | null>(null);
  const [selectedMapSite, setSelectedMapSite] = useState<any | null>(null);
  const [selectedLatLng, setSelectedLatLng] = useState<L.LatLng | null>(null);
  const [tilePosition, setTilePosition] = useState<{ x: number; y: number } | null>(null);

  const lastRequestRef = useRef<string>('');

  const siteStatsCards = [
    {
      id: 1,
      title: 'Total Projects',
      value: sitesStats?.totalProjects ?? 0,
      icon: Folder,
      iconBg: 'bg-[#5CC9A7]/50',
    },
    {
      id: 2,
      title: 'Total Sites',
      value: sitesStats?.totalSites ?? 0,
      icon: MapPin,
      iconBg: 'bg-[#2C8DF4]/80',
    },
    {
      id: 3,
      title: 'Active Sites',
      value: sitesStats?.activeSites ?? 0,
      icon: Shield,
      iconBg: 'bg-[#CC167C]/80',
    },
    {
      id: 4,
      title: 'Active Surveys',
      value: sitesStats?.activeSurveys ?? 0,
      icon: Eye,
      iconBg: 'bg-[#0BA96F]/50',
    },
  ];

  const displaySites = useMemo<(SiteByProjectIdDto | SiteListItemDto)[]>(() => {
    let sites: (SiteByProjectIdDto | SiteListItemDto)[] = [];

    if (selectedProject && selectedProjectsSitesData?.length > 0) {
      sites = selectedProjectsSitesData;
    } else {
      sites = sitesData ?? [];
    }

    //  FILTER BY SELECTED SITE
    if (selectedSite?.value) {
      sites = sites.filter((site: any) => site.id === selectedSite.value);
    }

    return sites;
  }, [selectedProject, selectedProjectsSitesData, sitesData, selectedSite]);

  const getProjectName = (site: SiteListItemDto | SiteByProjectIdDto) => {
    if ('project' in site) {
      if (typeof site.project === 'string') return site.project;
      return site.project?.name;
    }

    // when SiteByProjectIdDto (project selected)
    if (selectedProject) {
      return selectedProject.name;
    }

    return '';
  };

  const filteredSites = useMemo(() => {
    if (!searchQuery) return displaySites;

    const query = searchQuery.toLowerCase();

    return displaySites.filter((site) => {
      const siteName = site.name?.toLowerCase() ?? '';
      const countryName = site.country?.name?.toLowerCase() ?? '';
      const projectName = getProjectName(site)?.toLowerCase() ?? '';

      return siteName.includes(query) || countryName.includes(query) || projectName.includes(query);
    });
  }, [displaySites, searchQuery, selectedProject]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (!displaySites || displaySites.length === 0) {
      return [20.5937, 78.9629];
    }

    const sites = displaySites as HasGeofence[];

    const validSites = sites.filter(
      (s) =>
        s.geofence?.centerLat !== null &&
        s.geofence?.centerLng !== null &&
        typeof s.geofence.centerLat === 'number' &&
        typeof s.geofence.centerLng === 'number',
    );

    if (validSites.length === 0) {
      return [20.5937, 78.9629];
    }

    const avgLat =
      validSites.reduce((sum, s) => sum + s.geofence.centerLat!, 0) / validSites.length;

    const avgLng =
      validSites.reduce((sum, s) => sum + s.geofence.centerLng!, 0) / validSites.length;

    return [avgLat, avgLng];
  }, [displaySites]);

  const permissions = {
    create: usePermission('write', 'sites'),
    edit: usePermission('edit', 'sites'),
    delete: usePermission('delete', 'sites'),
  };

  useEffect(() => {
    dispatch(getSitesCount());
    dispatch(getProjectsDropdown());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedProject) {
      const requestKey = `${currentPage}-${pageSize}-${searchQuery}`;
      if (lastRequestRef.current === requestKey) return;

      lastRequestRef.current = requestKey;
      dispatch(
        fetchSitesList({
          page: currentPage,
          limit: pageSize,
        }),
      );
    }
  }, [dispatch, currentPage, pageSize, searchQuery, selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      dispatch(getSitesByProjectId(selectedProject?.id));
      setSelectedSite(null);
    }
  }, [selectedProject, dispatch]);

  const userMarkerIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl:
          'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      }),
    [],
  );

  const updateTilePosition = useCallback((pos: { x: number; y: number }) => {
    setTilePosition(pos);
  }, []);

  const handleFilterChange = useCallback((filters: Record<string, any>) => {
    const search = filters.search || '';
    const filter = filters.filterBy;

    setSearchQuery(search);
    setCurrentPage(1);

    if (filter === 'Sites') setFilterBy('Sites');
    else if (filter === 'Users') setFilterBy('Users');
    else setFilterBy(null);
  }, []);

  const totalPages = list.totalPages || 0;

  const handleViewSite = (site: any) => {
    setSelectedSite(site);
    setOpenSiteModal(true);
  };

  const handleMapMarkerClick = (site: Site | SiteListItemDto | SiteByProjectIdDto | SiteInfo) => {
    navigate('/dashboard/site-management/ProjectViewPage', {
      state: {
        siteId: site.id,
        projectId: site.projectId,
        from: 'site-management-map',
      },
    });
  };

  const getSiteDetailFields = (site: any) => {
    if (!site) return [];

    return [
      { label: 'Site Name', value: site.name || 'N/A', half: true },

      { label: 'Country', value: site.country?.name || 'N/A', half: true },

      {
        label: 'Longitude',
        value: site.geofence?.centerLng !== undefined ? site.geofence.centerLng : 'N/A',
        half: true,
      },

      {
        label: 'Latitude',
        value: site.geofence?.centerLat !== undefined ? site.geofence.centerLat : 'N/A',
        half: true,
      },

      {
        label: 'Radius',
        value:
          site.geofence?.radiusMeters !== undefined
            ? `${(site.geofence.radiusMeters / 1000).toFixed(2)} km`
            : 'N/A',
        half: true,
      },

      { label: 'Max Users', value: site.maxUsers ?? 'N/A', half: true },

      {
        label: 'Language(s)',
        value:
          Array.isArray(site.languages) && site.languages.length > 0
            ? site.languages.map((l: any) => l.name).join(', ')
            : 'N/A',
        colSpan: 2,
      },

      { label: 'Reward Currency', value: site.currency || 'N/A', half: true },

      { label: 'Project', value: site.project?.name || 'N/A', half: true },

      {
        label: 'Status',
        value: typeof site.status === 'boolean' ? (site.status ? 'Active' : 'Inactive') : 'N/A',
        badge: true,
        colSpan: 2,
      },
    ];
  };

  const handleEditSite = (site: Site) => {
    navigate('/dashboard/site-management/EditSite', {
      state: {
        siteId: site.id,
        projectId: site.projectId,
        from: 'site-management',
      },
    });
  };

  const handleToggleStatus = async (site: any) => {
    try {
      const newStatus = !site.status;

      await dispatch(
        toggleSiteStatus({
          id: site.id,
          status: newStatus,
        }),
      ).unwrap();

      if (newStatus) {
        showToast.warning('Site unarchived successfully.');
      } else {
        showToast.success('Site archived successfully.');
      }

      dispatch(
        fetchSitesList({
          page: currentPage,
          limit: pageSize,
          search: searchQuery,
          status: statusFilter === null ? undefined : statusFilter.value,
        }),
      );
    } catch (error: any) {
      showToast.error(error?.message || 'Failed to update site status');
    }
  };

  const handleSiteArrowClick = (site: any) => {
    navigate('/dashboard/site-management/EditSite', {
      state: {
        projectId: site.project?.id,
        siteId: site.id,
        from: 'site-management',
      },
    });
  };

  const usersData = useMemo(() => {
    if (!filteredSites?.length) return [];

    return filteredSites.flatMap((site) => site?.project?.users ?? []);
  }, [filteredSites]);

  return (
    <PageHeaderWrapper
      title="Site Management"
      description="View and monitor all your active sites."
    >
      <ViewDetailsModal
        isOpen={openSiteModal}
        onClose={() => setOpenSiteModal(false)}
        title="Site Details"
        data={selectedSite || {}}
        fields={getSiteDetailFields(selectedSite)}
      />

      <ServerSearchFilter
        fields={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search sites, country, or projects...',
          },
          {
            type: 'radio-select',
            key: 'filterBy',
            label: 'filter',
            options: ['Sites', 'Users'],
          },
        ]}
        onChange={handleFilterChange}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {siteStatsCards.map((stat) => (
          <StatCard
            key={stat.id}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            iconBg={stat.iconBg}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-[#4A082D]">
        <div>
          {/* <p className="w-full text-sm mb-1">Select Project</p> */}
          <DropdownWithRadio
            label="project"
            options={['All Projects', ...(projectsData?.map((project) => project.name) || [])]}
            selected={selectedProject?.name || null}
            onChange={(value) => {
              if (value === null) {
                setSelectedProject(null);
              } else {
                const project = projectsData?.find((p) => p.name === value);
                setSelectedProject(project || null);
              }
            }}
            isOpen={isProjectDropdownOpen}
            onOpen={() => setIsProjectDropdownOpen(true)}
            onClose={() => setIsProjectDropdownOpen(false)}
            placeholder="Select a project..."
          />
        </div>
        <div>
          {/* <p className="w-full text-sm mb-1">Select Site</p> */}

          <DropdownWithRadio
            label="site"
            options={['All Sites', ...filteredSites.map((site) => site.name)]}
            selected={selectedSite?.label || null}
            disabled={!selectedProject}
            onChange={(value) => {
              if (value === null) {
                setSelectedSite(null);
              } else {
                const site = (displaySites as any[]).find((s: any) => s.name === value);
                setSelectedSite(site ? { value: site.id, label: site.name } : null);
              }
            }}
            isOpen={isSiteDropdownOpen}
            onOpen={() => setIsSiteDropdownOpen(true)}
            onClose={() => setIsSiteDropdownOpen(false)}
            placeholder={
              selectedProject
                ? loadingSitesByProject
                  ? 'Loading sites...'
                  : displaySites.length === 0
                    ? 'No sites found for this project'
                    : 'Select a site from this project...'
                : 'Select a site...'
            }
          />
        </div>
      </div>

      {/* Map */}
      <div className="bg-[#F2EBEE] rounded-lg p-3">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-bold text-xl">Site Map</h2>
        </div>
        <div className="w-full h-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-5">
          <MapContainer
            center={mapCenter}
            zoom={3}
            style={{ height: '100%', width: '100%' }}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
            maxBoundsViscosity={1.0}
          >
            <TileLayer
              noWrap={true}
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {filterBy !== 'Users' &&
              filteredSites.map((site, idx: number) => {
                const lat = site?.geofence?.centerLat;
                const lng = site?.geofence?.centerLng;
                const radius = site?.geofence?.radiusMeters;

                if (typeof lat === 'number' && typeof lng === 'number') {
                  return (
                    <React.Fragment key={`site-${idx}`}>
                      {radius && (
                        <Circle
                          center={[lat, lng]}
                          radius={radius}
                          pathOptions={{
                            fillColor: '#621C43',
                            fillOpacity: 0.2,
                            color: '#621C43',
                            weight: 2,
                          }}
                        />
                      )}

                      <Marker
                        position={[lat, lng]}
                        eventHandlers={{
                          click: () => handleMapMarkerClick(site),
                        }}
                      >
                        <Popup>
                          <b>{site.name}</b>
                          <br />
                          {site?.country?.name}
                        </Popup>
                      </Marker>
                    </React.Fragment>
                  );
                }
                return null;
              })}

            {/* {filter by users } */}
            {filterBy !== 'Sites' &&
              usersData &&
              usersData.map((user: any, index: number) => {
                const lat = Number(user.latitude);
                const lng = Number(user.longitude);

                if (isNaN(lat) || isNaN(lng)) return null;

                return (
                  <Marker
                    key={`user-${user.userId}-${index}`}
                    position={[lat, lng]}
                    icon={userMarkerIcon}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">User ID: {user.userId}</p>
                        <p>Status: {user.status ? 'Active' : 'Inactive'}</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

            <MapEventsHandler
              selectedLatLng={selectedLatLng}
              setTilePosition={updateTilePosition}
            />
          </MapContainer>
        </div>
      </div>

      {/* Site Cards */}
      <div className="">
        {/* Grid of Sites */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {filteredSites.map((site) => (
            <SiteInfoCard
              key={site.id}
              site={site}
              onView={handleViewSite}
              handleArrowClick={handleSiteArrowClick}
              onToggleStatus={handleToggleStatus}
              permissions={permissions}
            />
          ))}

          {/* See More Card */}
          {!selectedProject &&
            !searchQuery &&
            sitesData &&
            sitesData.length > 0 &&
            currentPage < totalPages && (
              <button
                onClick={() => navigate('/dashboard/site-management/all-sites')}
                className="relative flex flex-col justify-between bg-[#AFAFAF] text-[#2B1C25] rounded-lg shadow hover:bg-[#9e8895] transition-all p-5 w-full"
              >
                <div className="flex flex-col items-start">
                  <span className="text-2xl font-bold">See More</span>
                  <span className="text-sm">
                    {list?.total! - 3} site
                    {list?.total! - 3 !== 1 ? 's' : ''} remaining
                  </span>
                </div>
                <ArrowDown
                  className="absolute bottom-4 right-4 bg-transparent cursor-pointer p-1 rounded text-[#2B1C25]"
                  height={44}
                  width={44}
                />
              </button>
            )}
        </div>
      </div>

      {/* {Reusable tile} */}
      {selectedMapSite && tilePosition && (
        <MapSiteTile
          site={selectedMapSite}
          position={tilePosition}
          onView={(site) => {
            const str: string =
              'Latitide - ' +
              site.geofence.centerLat +
              ' \n ' +
              'Longitude - ' +
              site.geofence.centerLng;
            showToast.info(str);
          }}
          onEdit={(site) => handleEditSite(site)}
          onClose={() => setSelectedMapSite(null)}
        />
      )}
    </PageHeaderWrapper>
  );
}
